import '@webcomponents/shadycss/entrypoints/apply-shim.js';

const styleElement = document.createElement('dom-module');
styleElement.innerHTML =
`<template>
<style>
  :host {
    position: relative;
    display: block;
    cursor: pointer;
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    padding-top: var(--com-calendar-fuse-padding-top, 3px);
    @apply --type-base;
    @apply --com-calendar-date-general-styles;

    /* action state */
    background: var(--com-calendar-date-action-background, white);
    color: var(--com-calendar-date-action-color, var(--primary-action, #0079BC));
    outline: none;
    z-index: 1;
  }

  :host([disable-user-interaction]) {
    cursor: auto;
  }

  :host(.none) {
    pointer-events: none;
  }

  :host(:not([allow-disabled-focus]) .blocked),
  :host(:not([allow-disabled-focus]) .unavailable) {
    pointer-events: none;
  }

  :host([loading]) {
    pointer-events: none;
  }

  /* disabled state, allow-disabled-focus */
  :host([disabled]),
  :host([allow-disabled-focus]) {
    cursor: default;
    background: var(--com-calendar-date-disabled-background, white);
    color: var(--com-calendar-date-disabled-color, var(--inactive-grey));
    outline: none;
  }

  :host([disabled][past]),
  :host([allow-disabled-focus][past]) {
    cursor: default;
    background: var(--com-calendar-date-past-background, white);
    color: var(--com-calendar-date-past-color, var(--inactive-grey));
  }

  /* selected/hover states need to have border above surrounding dates */
  :host(:not([allow-disabled-focus]):hover) {
    z-index: 2
  }

  :host(:not([selected]):not([allow-disabled-focus]):focus) {
    z-index: 3;
  }

  :host([selected]:not([disabled])) {
    z-index: 4;
    transform: scale(var(--com-calendar-date-selected-scale-rate, 1.1));
    border-radius: 3px;
  }

  /* selected state */
  :host([selected]:not([disabled])),
  :host([allow-unavailable-selection]:[selected]) {
    background: var(--com-calendar-date-active-background, var(--primary-active));
    border: var(--com-calendar-date-active-border, var(--primary-active));
    color: var(--com-calendar-date-active-color, white);
    @apply --com-calendar-date-selected-styles;
  }

  /* hover & focus state */
  :host(:not([selected]):not([allow-disabled-focus]):not([loading]):not([navigation="swipe"]):hover),
  :host(:not([disabled]):not([allow-disabled-focus]):not([selected]):focus) {
    background: var(--com-calendar-date-hover-background, white);
    border: var(--com-calendar-date-hover-border-width, 2px) solid var(--com-calendar-date-hover-border, var(--primary-hover));
    color: var(--com-calendar-date-hover-color, var(--primary-action, #0079BC));
    @apply --no-highlight-selection;
  }

  /* hover state for implementing specific hover styles different from focus state*/
  :host(:not([selected]):not([allow-disabled-focus]):not([loading]):not([navigation="swipe"]):not(:focus):hover) {
    @apply --com-calendar-date-hover-styles;
  }


  /* hover state for implementing specific focus styles different from hover state*/
  :host(:not([selected]):not([allow-disabled-focus]):not([loading]):not([navigation="swipe"]):not(:hover):focus) {
    @apply --com-calendar-date-focus-styles;
  }

  /* encapsulate position:relative away from :host */
  #date-container {
    position: relative;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    @apply --com-calendar-date-container-styles;
  }

  :host(:focus) #date-container {
    @apply --com-calendar-date-container-focus-styles;
  }

  :host(:not([disabled]):not([allow-disabled-focus])[selected]:not(:hover):focus) #date-container {
    @apply --com-calendar-date-container-selected-focus-styles;
  }

  :host(:not([disabled]):not([allow-disabled-focus]):not([selected]):not(:hover):focus) #date-container {
    @apply --com-calendar-date-container-active-focus-styles;
  }

  :host(:not([selected]):not([allow-disabled-focus]):not([loading]):not([navigation="swipe"]):not(:focus):hover) #date-container {
    @apply --com-calendar-date-container-active-hover-styles;
  }

  :host([selected]) #date-container {
    @apply --com-calendar-date-container-selected-styles;
  }

  /* day number in top-right corner */
  .number {
    position: absolute;
    top: 5px;
    right: 10px;
    @apply --type20;
    font-weight: var(--com-calendar-date-custom-font-weight, 100);
    @apply --user-select-none;
    color: var(--com-calendar-date-action-color, var(--primary-action, #0079BC));
    @apply --com-calendar-date-number-styles;
  }

  :host([disabled]) .number,
  :host([allow-disabled-focus]) .number {
    color: var(--com-calendar-date-disabled-number-color, var(--inactive-grey));
  }

  :host([disabled][past]) .number,
  :host([allow-disabled-focus][past]) .number {
    color: var(--com-calendar-date-past-number-color, var(--inactive-grey));
  }

  :host(:not([adaptive="xs"]):not([disable-user-interaction]):not([allow-disabled-focus]):not([selected]):not([navigation="swipe"]):hover) .number,
  :host(:not([disable-user-interaction]):not([allow-disabled-focus]):not([disabled]):not([selected]):focus) .number {
    color: var(--com-calendar-date-hover-number-color, var(--primary-action, #0079BC));
  }

  :host([selected]:not([disabled])) {
    transform: scale(var(--com-calendar-date-selected-scale-rate, 1.1));
    border-radius: 0;
  }

  :host([selected]:not([disabled])) .number {
    top: 0;
  }

  :host([past]) .number {
    @apply --com-calendar-date-compact-past-number-styles;
  }

  #loadingWrapper {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    height: 100%;
    box-sizing: border-box;
    padding: 6px;
    color: var(--inactive-grey);
    font-size: 12px;
    line-height: 0.5em;
  }

  /* subtract a pixel from position because hover border is 1px larger */
  :host(:not([selected]):hover) #loadingWrapper,
  :host(:not([selected]):focus) #loadingWrapper {
    padding: 5px;
  }

  :host #slotWrapper {
    display: none;
  }

  :host .number {
    top: auto;
    position: relative;
    padding: var(--com-calendar-date-number-padding, 8px);
    right: auto;
    margin: 0 auto;
    width: 27px;
    text-align: center;
    height: 27px;
    border-radius: 50%;
  }

  :host #date-container {
    padding-right: var(--com-calendar-date-container-padding-right, 0);
    padding-left: var(--com-calendar-date-container-padding-left, 0);
    margin-right: 0;
    margin-left: 0;
  }
  :host .dateTypeIndicatorContainer {
    padding-right: var(--com-calendar-date-indicator-padding-right, 0);
    padding-left: var(--com-calendar-date-indicator-padding-left, 0);
  }

  :host(.selectable.end) #date-container {
    padding-right: 0;
    margin-right: var(--com-calendar-date-container-padding-right, 0);
  }

  :host(.selectable.start) #date-container {
    padding-left: 0;
    margin-left: var(--com-calendar-date-container-padding-left, 0);
    width: auto;
    border-radius: 30px 0 0 30px;
    border-left: 1px solid var(--inactive-grey);
  }

  :host(:not([adaptive="xs"]):not([disable-user-interaction]):not([selected]):hover) .number,
  :host(:not([disable-user-interaction]):not([selected]):focus) .number {
    top: 0;
    right: 0;
    @apply --com-calendar-date-number-focus-styles;
    width: 27px;
    text-align: center;
    background-color: var(--primary-action, #0079BC);
  }

  /*special class for unavailable and none class*/
 
  :host(:not([disable-user-interaction]):not([selected]).none:focus) .number,
  :host(:not([disable-user-interaction]):not([selected]).unavailable:focus) .number {
    top: 0;
    right: 0;
    @apply --com-calendar-date-number-focus-styles;
    width: 27px;
    text-align: center;
    color: var(--com-calendar-date-active-number-color, white);
    background-color:  var(--com-calendar-date-disabled-color, #ACBAC1);
  }

  /*special class for noInfo class. show no focus property*/

  :host(:not([disable-user-interaction]):not([selected]).noInfo:focus) .number {
    top: 0;
    right: 0;
    width: 27px;
    text-align: center;
    background-color:  var(--com-calendar-date-active-number-color, white);
  }

  :host(:not([disable-user-interaction])[selected]) .number,
  :host(:not([adaptive="xs"]):not([disable-user-interaction])[selected]:hover) .number,
  :host(:not([disable-user-interaction])[selected]:focus) .number,
  :host(:not([disable-user-interaction])[allow-disabled-focus][selected]) .number,
  :host(:not([disable-user-interaction])[allow-disabled-focus][selected]:focus) .number
   {
    background-color: var(--primary-active);
    color: var(--com-calendar-date-active-number-color, white);
    @apply --com-calendar-date-number-selected-styles;
  }

  .padding,
  .number .slash {
    display: none;
  }

  :host(.blocked) .number .slash,
  :host(:not([slash-on-blocked-only]).unavailable) .number .slash {
    display: block;
    position: absolute;
    width: var(--com-calendar-date-slash-dimension-percent, 70%);
    height: var(--com-calendar-date-slash-dimension-percent, 70%);
    top: var(--com-calendar-date-slash-start-offset, 15%);
    left: var(--com-calendar-date-slash-start-offset, 15%);
  }

  /* :host(.selectable.start) .padding,
  :host(.selectable.end) .padding, */
  :host([selected]:not([disabled]).selectable) #date-container,
  :host(.selectable) #date-container {
    background-color: var(--background-component);
    border-top: 1px solid var(--inactive-grey);
    border-bottom: 1px solid var(--inactive-grey);
  }

  :host(.selectable.end) #date-container {
    width: auto;
    border-radius: 0 30px 30px 0;
    border-right: 1px solid var(--inactive-grey);
  }

  :host(.selectable.end) .dateContainerOuter {
    padding-right: 1px;
  }  

  :host(.selectable.start) .dateContainerOuter {
    padding-left: 1px;
  }

  :host(.selectable.start) .dateContainerInner,
  :host(.selectable.end) .dateContainerInner {
    justify-content: center;
    display: flex;
    position: relative;
  }

  :host(.selectable.start) .padding {
    display: block;
    width: 43%;
    height: var(--com-calendar-fuse-height, 43px);
    position: absolute;
    top: 0;
    right: 0;
    z-index: -2;
    border-top: 1px solid var(--inactive-grey);
    border-bottom: 1px solid var(--inactive-grey);
    background-color:var(--background-component);
  }
  :host(.selectable.end) .padding {
    display: block;
    width: 43%;
    height: var(--com-calendar-fuse-height, 43px);
    position: absolute;
    top: 0;
    left: 0;
    z-index: -2;
    border-top: 1px solid var(--inactive-grey);
    border-bottom: 1px solid var(--inactive-grey);
    background-color:var(--background-component);
  }

  :host(.dimmed) {
    opacity: 0.3;
    pointer-events: none;
  }

  :host(.blocked) .number .slash line,
  :host(.unavailable) .number .slash line {
    stroke-linecap: round;
    stroke-width: 2;
    stroke: var(--com-calendar-date-disabled-color, #ACBAC1);
  }

  :host(.gtg) .number::before {
    position: absolute;
    content: "";
    top: 1px;
    right: 1px;
    bottom: 1px;
    left: 1px;
    border: 2px solid #008900;
    border-radius: 50%;
  }

  :host(.gtg[selected]) .number {
    border-color: var(--primary-active, #00233C);
  }

  :host(.gtg:not([selected]):hover) .number {
    border-color: var(--primary-action, #006096);
  }

  :host(.gtg[selected]) .number::before,
  :host(.gtg:hover) .number::before {
    position: absolute;
    content: "";
    top: 2px;
    right: 2px;
    bottom: 2px;
    left: 2px;
    border: 2px solid white;
    border-radius: 50%;
  }

  :host([allow-disabled-focus].blocked:focus) .number,
  :host([allow-disabled-focus]:not([selected]).blocked:hover) .number,
  :host([allow-disabled-focus][selected].blocked) .number .slash line,
  :host([allow-disabled-focus].blocked:focus) .number .slash line,
  :host([allow-disabled-focus].blocked:hover) .number .slash line,
  :host([allow-disabled-focus].noInfo:focus) .number,
  :host([allow-disabled-focus].noInfo:focus) .number .slash line,
  :host([allow-disabled-focus].noInfo:hover) .number .slash line,
  :host([allow-disabled-focus].unavailable:focus) .number,
  :host([allow-disabled-focus]:not([selected]).unavailable:hover) .number,
  :host([allow-disabled-focus][selected].unavailable) .number .slash line,
  :host([allow-disabled-focus].unavailable:focus) .number .slash line,
  :host([allow-disabled-focus].unavailable:hover) .number .slash line {
    color: white;
    stroke: white;
    background-color: var(--primary-action, #0079BC);
  }

  :host([allow-disabled-focus][selected].blocked) .number,
  :host([allow-disabled-focus][selected].blocked:focus) .number,
  :host([allow-disabled-focus][selected].blocked:hover) .number,
  :host([allow-disabled-focus][selected].noInfo) .number,
  :host([allow-disabled-focus][selected].noInfo:focus) .number,
  :host([allow-disabled-focus][selected].noInfo:hover) .number,
  :host([allow-disabled-focus][selected].unavailable) .number,
  :host([allow-disabled-focus][selected].unavailable:focus) .number,
  :host([allow-disabled-focus][selected].unavailable:hover) .number {
    background-color: var(--primary-active);
    color: var(--com-calendar-date-active-number-color, white);
    @apply --com-calendar-date-number-selected-styles;
  }

  @keyframes com-calendar-date-selected-date {
    0% {
      transform: scale(1);
      opacity: 0;
    }
    15% {
      transform: scale(1.1);
      opacity: 0.3;
    }
    33% {
      transform: scale(1.2);
      opacity: 0.6;
    }
    66% {
      transform: scale(1.075);
      opacity: 1;
    }
    100% {
      transform: scale(var(--com-calendar-date-selected-scale-rate, 1.1));
    }
  }

  @keyframes com-calendar-date-loading {
    0% {
      opacity: 1;
    }
    15% {
      opacity: 0.66;
    }
    33% {
      opacity: .33;
    }
    66% {
      opacity: 0.66;
    }
    100% {
      opacity: 1;
    }
  }

  :host([selected-date-animation]) {
    visibility: visible;
    animation: com-calendar-date-selected-date .5s ease;
  }

  :host([loading]) .loading {
    animation: com-calendar-date-loading 2s ease infinite;
  }
</style>
</template>`;

styleElement.register('com-calendar-date-style');